import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Mountain, Loader2 } from "lucide-react";

export function AuthModal({ open, onClose, defaultHost = false }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isHost, setIsHost] = useState(defaultHost);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Bine ai revenit!");
      } else {
        await register({ email, password, name, is_host: isHost });
        toast.success("Cont creat cu succes!");
      }
      onClose();
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const fillDemo = (type) => {
    if (type === "guest") { setEmail("oaspete@airbnmountain.ro"); setPassword("demo1234"); }
    else { setEmail("gazda@airbnmountain.ro"); setPassword("demo1234"); }
    setMode("login");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0" data-testid="auth-modal">
        <div className="bg-primary text-primary-foreground px-6 py-5 flex items-center gap-3">
          <Mountain className="h-6 w-6" />
          <div>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl leading-none">
                {mode === "login" ? "Bun venit înapoi" : "Creează un cont"}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-primary-foreground/80 mt-1">Airbnmountain · Carpați</p>
          </div>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label htmlFor="name">Nume complet</Label>
              <Input id="name" data-testid="auth-name-input" value={name} required
                     onChange={(e) => setName(e.target.value)} placeholder="Ion Popescu" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="auth-email-input" value={email} required
                   onChange={(e) => setEmail(e.target.value)} placeholder="nume@exemplu.ro" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Parolă</Label>
            <Input id="password" type="password" data-testid="auth-password-input" value={password} required
                   onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          {mode === "register" && (
            <div className="flex items-center justify-between rounded-xl bg-muted px-4 py-3">
              <div>
                <p className="text-sm font-medium">Vreau să devin gazdă</p>
                <p className="text-xs text-muted-foreground">Adaugă anunțuri și primește rezervări</p>
              </div>
              <Switch checked={isHost} onCheckedChange={setIsHost} data-testid="auth-host-switch" />
            </div>
          )}

          {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

          <Button type="submit" disabled={loading} data-testid="auth-submit-button"
                  className="w-full h-11 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full text-base">
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {mode === "login" ? "Autentifică-te" : "Înregistrează-te"}
          </Button>

          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">sau</span>
            </div>
          </div>

          <Button type="button" variant="outline" onClick={googleLogin} data-testid="auth-google-button"
                  className="w-full h-11 rounded-full gap-2">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
            Continuă cu Google
          </Button>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={() => fillDemo("guest")}
                    className="flex-1 text-xs rounded-lg border border-border py-2 hover:bg-muted transition-colors" data-testid="demo-guest-button">
              Demo Oaspete
            </button>
            <button type="button" onClick={() => fillDemo("host")}
                    className="flex-1 text-xs rounded-lg border border-border py-2 hover:bg-muted transition-colors" data-testid="demo-host-button">
              Demo Gazdă
            </button>
          </div>

          <p className="text-center text-sm text-muted-foreground">
            {mode === "login" ? "Nu ai cont?" : "Ai deja cont?"}{" "}
            <button type="button" data-testid="auth-toggle-mode"
                    onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                    className="text-accent font-medium hover:underline">
              {mode === "login" ? "Înregistrează-te" : "Autentifică-te"}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
